import { ReactNode, useId } from "react";


/**
 * FramedSection, sitewide editorial chrome that runs four vertical
 * dashed hairlines (left safe-area, content-left, content-right, right
 * safe-area) plus top/bottom horizontal dashed hairlines through every
 * page section, AND draws `+` crosshair corner marks at the four
 * intersections where the horizontals meet the inner content verticals.
 *
 * Color comes from the existing `--foreground` brand-dark token at:
 *   - dashed lines: 18%
 *   - `+` corner marks: 32%
 *
 * Optionally renders a faded background pattern (zed.dev-style) behind
 * the section content. Patterns are inline SVG with currentColor so they
 * inherit the `text-foreground` token and respect light/dark mode
 * automatically, no Tailwind JIT issues, no dynamic class names.
 */
type PatternVariant = "noise" | "hatch" | "grid" | "dots" | "waves";

interface FramedSectionProps {
  children: ReactNode;
  /** Omit the top horizontal hairline (use on the section after the nav). */
  noTop?: boolean;
  /** Omit the bottom horizontal hairline (use on the section before the footer). */
  noBottom?: boolean;
  /** Render 16px diagonal-hatch bands at the top and bottom of the section. Default true. */
  hatchBands?: boolean;
  /** Decorative background pattern layered behind content (very faded). */
  pattern?: PatternVariant;
  /** Subtle brand-color wash layered above the bg base, below the pattern overlay. */
  gradient?: boolean;
  /** Optional extra classes for the outer wrapper. */
  className?: string;
  /** Optional id passthrough for in-page anchors. */
  id?: string;
}

const DASH_STROKE = "hsl(var(--foreground) / 0.07)";
const MARK_STROKE = "hsl(var(--foreground) / 0.14)";

/** Vertical SVG dashed line, full height of the section. */
const VerticalDash = ({
  position,
}: {
  position: { left?: string; right?: string };
}) => (
  <svg
    aria-hidden
    className="hidden md:block pointer-events-none"
    preserveAspectRatio="none"
    viewBox="0 0 1 100"
    style={{
      position: "absolute",
      top: 0,
      bottom: 0,
      width: 1,
      height: "100%",
      zIndex: 0,
      ...position,
    }}
  >
    <line
      x1="0.5"
      y1="0"
      x2="0.5"
      y2="100"
      stroke={DASH_STROKE}
      strokeWidth="1"
      strokeDasharray="6 4"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

/** Horizontal SVG dashed line, full viewport-wide. */
const HorizontalDash = ({ position }: { position: "top" | "bottom" }) => (
  <svg
    aria-hidden
    className="pointer-events-none"
    preserveAspectRatio="none"
    viewBox="0 0 100 1"
    style={{
      position: "absolute",
      [position]: 0,
      left: "-100vw",
      width: "200vw",
      height: 1,
      zIndex: 0,
    }}
  >
    <line
      x1="0"
      y1="0.5"
      x2="100"
      y2="0.5"
      stroke={DASH_STROKE}
      strokeWidth="1"
      strokeDasharray="6 4"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

/** `+` crosshair mark, centered on (left/right, top/bottom) corner. */
const CornerMark = ({
  v,
  h,
}: {
  v: "top" | "bottom";
  h: { left?: string; right?: string };
}) => {
  const size = 10;
  const half = size / 2;
  return (
    <svg
      aria-hidden
      className="hidden md:block pointer-events-none"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{
        position: "absolute",
        [v]: -half,
        ...h,
        marginLeft: h.left !== undefined ? -half : undefined,
        marginRight: h.right !== undefined ? -half : undefined,
        zIndex: 1,
      }}
    >
      <line x1="0" y1={half} x2={size} y2={half} stroke={MARK_STROKE} strokeWidth="1" />
      <line x1={half} y1="0" x2={half} y2={size} stroke={MARK_STROKE} strokeWidth="1" />
    </svg>
  );
};

/**
 * BackgroundPattern, inline SVG <pattern> tile, drawn with currentColor so
 * it inherits the `text-*` class on the parent SVG. Geometry is lifted
 * directly from zed.dev's primitives (8px dots / 10px grid / 6px hatch).
 */
const BackgroundPattern = ({ variant }: { variant: PatternVariant }) => {
  const uid = useId();
  const patternId = `fs-pattern-${uid.replace(/:/g, "")}-${variant}`;
  const filterId = `fs-filter-${uid.replace(/:/g, "")}-noise`;

  if (variant === "noise") {
    return (
      <>
        {/* Inner content base: white in both light and dark modes. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-background"
          style={{ zIndex: 0 }}
        />
        {/* Grain: multiply dark specks onto white paper (same in both modes). */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-pattern-noise-grain"
          style={{ zIndex: 0, opacity: 0.05 }}
        />
      </>
    );
  }

  // Tile size per variant
  const tile =
    variant === "dots"
      ? { w: 22, h: 22 }
      : variant === "grid"
      ? { w: 28, h: 28 }
      : variant === "waves"
      ? { w: 40, h: 20 }
      : /* hatch */ { w: 14, h: 14 };

  // Per-variant overlay opacity, very faint, barely-there texture.
  const overlayOpacity = 0.04;

  return (
    <>
      {/* White base in both light and dark modes (inner content area only). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-background"
        style={{ zIndex: 0 }}
      />
      {/* Top layer: faint pattern overlay */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 size-full"
        style={{ zIndex: 0, opacity: overlayOpacity, color: "#0a0a0a" }}
      >
        <defs>
          <pattern
            id={patternId}
            patternUnits="userSpaceOnUse"
            width={tile.w}
            height={tile.h}
          >
            {variant === "dots" && (
              <circle cx="11" cy="11" r="0.9" fill="currentColor" />
            )}
            {variant === "grid" && (
              <path
                d="M.5 28V.5H28"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.6"
              />
            )}
            {variant === "hatch" && (
              <line
                x1="0"
                y1="14"
                x2="14"
                y2="0"
                stroke="currentColor"
                strokeWidth="0.6"
              />
            )}
            {variant === "waves" && (
              <>
                <path d="M0 20 A10 10 0 0 1 20 20" fill="none" stroke="currentColor" strokeWidth="0.7" />
                <path d="M20 20 A10 10 0 0 1 40 20" fill="none" stroke="currentColor" strokeWidth="0.7" />
                <path d="M-10 0 A10 10 0 0 1 10 0" fill="none" stroke="currentColor" strokeWidth="0.7" />
                <path d="M10 0 A10 10 0 0 1 30 0" fill="none" stroke="currentColor" strokeWidth="0.7" />
                <path d="M30 0 A10 10 0 0 1 50 0" fill="none" stroke="currentColor" strokeWidth="0.7" />
              </>
            )}
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>
    </>
  );
};

const FramedSection = ({
  children,
  noTop,
  noBottom,
  hatchBands = true,
  pattern,
  gradient,
  className,
  id,
}: FramedSectionProps) => {
  // Inner content column edges where `+` marks anchor.
  const innerLeft = "calc(50% - 640px)";
  const innerRight = "calc(50% - 640px)";

  return (
    <section
      id={id}
      className={["relative w-full", className].filter(Boolean).join(" ")}
    >
      {/* Decorative background pattern, constrained to the inner content
          column so the outer safe-area gutters stay clean. */}
      {pattern && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
          style={{ zIndex: 0 }}
        >
          <BackgroundPattern variant={pattern} />
          {/* Painterly blob wash, sits ABOVE the white base but BELOW the
              grain (grain uses mix-blend multiply so it still reads on top).
              Light-mode only; brand palette only: cream, amber, taupe. */}
          {gradient && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 dark:hidden"
              style={{ zIndex: 1 }}
            >
              <div
                className="absolute rounded-full blur-3xl"
                style={{
                  top: "-180px",
                  left: "-160px",
                  width: "720px",
                  height: "720px",
                  background: "hsl(38 55% 78% / 0.55)",
                }}
              />
              <div
                className="absolute rounded-full blur-3xl"
                style={{
                  top: "60px",
                  right: "-120px",
                  width: "620px",
                  height: "620px",
                  background: "hsl(36 60% 72% / 0.45)",
                }}
              />
              <div
                className="absolute rounded-full blur-3xl"
                style={{
                  bottom: "-140px",
                  left: "30%",
                  width: "560px",
                  height: "560px",
                  background: "hsl(28 28% 70% / 0.50)",
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Horizontal dashed lines (run beyond viewport edges) */}
      {!noTop && <HorizontalDash position="top" />}
      {!noBottom && <HorizontalDash position="bottom" />}

      {/* Four vertical dashed columns */}
      <VerticalDash position={{ left: "24px" }} />
      <VerticalDash position={{ left: innerLeft }} />
      <VerticalDash position={{ right: innerRight }} />
      <VerticalDash position={{ right: "24px" }} />

      {/* `+` corner marks where horizontals meet the INNER content verticals */}
      {!noTop && (
        <>
          <CornerMark v="top" h={{ left: innerLeft }} />
          <CornerMark v="top" h={{ right: innerRight }} />
        </>
      )}
      {!noBottom && (
        <>
          <CornerMark v="bottom" h={{ left: innerLeft }} />
          <CornerMark v="bottom" h={{ right: innerRight }} />
        </>
      )}

      {/* Diagonal hatch bands, flush against the section's top/bottom hairline */}
      {hatchBands && !noTop && (
        <div
          aria-hidden
          className="bg-diagonal-hatch pointer-events-none absolute left-0 right-0 top-0"
          style={{ height: 16, opacity: 0.18, zIndex: 0 }}
        />
      )}
      {hatchBands && !noBottom && (
        <div
          aria-hidden
          className="bg-diagonal-hatch pointer-events-none absolute left-0 right-0 bottom-0"
          style={{ height: 16, opacity: 0.18, zIndex: 0 }}
        />
      )}

      {/* Inner content column */}
      <div className="relative z-[1] max-w-[1280px] mx-auto px-0 md:px-8">
        {children}
      </div>
    </section>
  );
};

export default FramedSection;
